import {
  ChevronLeftIcon,
  ChevronRightIcon,
  placeholder,
  PlayCircleIcon,
} from "@";
import type { Attachment } from "@/features/operations/verifications/types.ts";
import { AttachmentType } from "@/features/operations/verifications/types.ts";
import { useEffect, useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";

interface MediaStageProps {
  attachments: Attachment[];
  attIdx: number;
  setAttIdx: (idx: number) => void;
  goPrevAttachment: () => void;
  goNextAttachment: () => void;
  setMediaLoading: (v: boolean) => void;
}

export const MediaStage = ({
  attachments,
  attIdx,
  setAttIdx,
  goPrevAttachment,
  goNextAttachment,
  setMediaLoading,
}: MediaStageProps) => {
  const swiperRef = useRef<SwiperClass | null>(null);

  // External attIdx changes (chevrons, thumbnails, keyboard) → sync Swiper.
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed) return;
    if (swiper.activeIndex !== attIdx) {
      swiper.slideTo(attIdx);
    }
  }, [attIdx]);

  return (
    <section className="flex min-h-96 flex-1 flex-col">
      <div className="relative flex min-h-0 flex-1 select-none items-center justify-center overflow-hidden px-2">
        <Swiper
          dir="rtl"
          slidesPerView={1}
          spaceBetween={0}
          initialSlide={attIdx}
          onSwiper={(s) => {
            swiperRef.current = s;
          }}
          onSlideChange={(s) => {
            if (s.activeIndex !== attIdx) {
              setMediaLoading(true);
              setAttIdx(s.activeIndex);
            }
          }}
          className="h-full w-full"
        >
          {attachments.map((att, idx) => (
            <SwiperSlide
              key={`${att.url}-${idx}`}
              className="!flex items-center justify-center"
            >
              {att.type === AttachmentType.Image ? (
                <LazyLoadImage
                  alt=""
                  src={att.url || placeholder}
                  placeholderSrc={placeholder}
                  effect="opacity"
                  afterLoad={() => {
                    if (idx === attIdx) setMediaLoading(false);
                  }}
                  onError={() => {
                    if (idx === attIdx) setMediaLoading(false);
                  }}
                  onClick={(e: React.MouseEvent<HTMLImageElement>) =>
                    e.stopPropagation()
                  }
                  wrapperClassName="!flex h-full w-full items-center justify-center"
                  className="relative z-0 max-h-full max-w-full rounded-2xl object-contain"
                />
              ) : (
                <video
                  className="relative z-0 max-h-full max-w-full rounded-2xl"
                  autoPlay={idx === attIdx}
                  muted
                  controls
                  onLoadedData={() => {
                    if (idx === attIdx) setMediaLoading(false);
                  }}
                  onError={() => {
                    if (idx === attIdx) setMediaLoading(false);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <source src={att.url} type="video/mp4" />
                  <source src={att.url} type="video/quicktime" />
                </video>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {attachments.length === 0 && (
          <div className="text-sm text-white/60">لا توجد مرفقات</div>
        )}

        {attachments.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNextAttachment();
              }}
              aria-label="المرفق التالي"
              className="absolute end-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-gray-700 p-2.5 text-white/80 ring-1 ring-white/20 backdrop-blur-md transition hover:scale-105 hover:bg-white/20 hover:text-white"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrevAttachment();
              }}
              aria-label="المرفق السابق"
              className="absolute start-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-gray-700 p-2.5 text-white/80 ring-1 ring-white/20 backdrop-blur-md transition hover:scale-105 hover:bg-white/20 hover:text-white"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}

        {attachments.length > 0 && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute start-4 top-4 z-20 rounded-full bg-black/50 px-3 py-1 text-xs tabular-nums text-white/90 ring-1 ring-white/15 backdrop-blur-md"
          >
            {attIdx + 1} / {attachments.length}
          </div>
        )}
      </div>

      {attachments.length > 1 && (
        <ThumbnailStrip
          attachments={attachments}
          attIdx={attIdx}
          setAttIdx={setAttIdx}
          setMediaLoading={setMediaLoading}
        />
      )}
    </section>
  );
};

interface ThumbnailStripProps {
  attachments: Attachment[];
  attIdx: number;
  setAttIdx: (idx: number) => void;
  setMediaLoading: (v: boolean) => void;
}

const ThumbnailStrip = ({
  attachments,
  attIdx,
  setAttIdx,
  setMediaLoading,
}: ThumbnailStripProps) => (
  <div
    onClick={(e) => e.stopPropagation()}
    className="mt-3 w-full overflow-x-auto p-1"
  >
    <div className="mx-auto flex w-fit items-center gap-2">
      {attachments.map((att, idx) => {
        const active = idx === attIdx;
        return (
          <button
            key={`${att.url}-${idx}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (idx !== attIdx) setMediaLoading(true);
              setAttIdx(idx);
            }}
            className={`group relative h-16 w-20 shrink-0 overflow-hidden rounded-lg ring-2 transition ${active ? "shadow-md shadow-purple-500/30 ring-purple-400" : "ring-white/15 hover:ring-white/40"}`}
          >
            {att.type === AttachmentType.Image ? (
              <LazyLoadImage
                src={att.url || placeholder}
                alt=""
                placeholderSrc={placeholder}
                effect="opacity"
                wrapperClassName="block h-full w-full"
                className="h-full w-full object-cover"
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  (e.currentTarget as HTMLImageElement).src = placeholder;
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900">
                <PlayCircleIcon className="h-7 w-7 text-white/80" />
              </div>
            )}
            {!active && (
              <div className="absolute inset-0 bg-black/40 transition group-hover:bg-black/10" />
            )}
          </button>
        );
      })}
    </div>
  </div>
);
